import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

import { SalesRep, SupplierBranch, TaxExemptionTeamMember } from '@dill/dill-shared';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import { SalesRepInitialState } from '../globalSlices/supplierAdditionalDetailsSlice';
import { TaxExemptionTeamInitialState } from '../globalSlices/taxExemptionTeamSlice';
import { SupplierBranchesInitialState } from '../globalSlices/supplierBranchesSlice';
import {
  getSupplierSalesReps,
  createSalesRep,
  updateSalesRep,
  deleteSalesRep,
  getSupplierTaxExemptionTeam,
  createTaxExemptionTeamMember,
  updateTaxExemptionTeamMember,
  deleteTaxExemptionTeamMember,
  getSupplierBranches,
  updateSupplierBranches,
} from '../services/supplierAdditionalDetailsService';

export const getSupplierSalesRepsExtraReducer = (
  builder: ActionReducerMapBuilder<SalesRepInitialState>
) => {
  const reducerId = 'getSupplierSalesReps';
  builder.addCase(getSupplierSalesReps.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getSupplierSalesReps.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.salesReps = action.payload.data as SalesRep[];
      }
    }
  );
  builder.addCase(getSupplierSalesReps.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const createSalesRepExtraReducer = (
  builder: ActionReducerMapBuilder<SalesRepInitialState>
) => {
  const reducerId = 'createSalesRep';
  builder.addCase(createSalesRep.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    createSalesRep.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        // The API returns the supplier's full salesReps array (a create/update/delete mutates
        // one array field on a single per-supplier doc, not a standalone document per rep).
        state.salesReps = action.payload.data as SalesRep[];
      }
    }
  );
  builder.addCase(createSalesRep.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const updateSalesRepExtraReducer = (
  builder: ActionReducerMapBuilder<SalesRepInitialState>
) => {
  const reducerId = 'updateSalesRep';
  builder.addCase(updateSalesRep.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    updateSalesRep.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.salesReps = action.payload.data as SalesRep[];
      }
    }
  );
  builder.addCase(updateSalesRep.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const deleteSalesRepExtraReducer = (
  builder: ActionReducerMapBuilder<SalesRepInitialState>
) => {
  const reducerId = 'deleteSalesRep';
  builder.addCase(deleteSalesRep.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    deleteSalesRep.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.salesReps = action.payload.data as SalesRep[];
      }
    }
  );
  builder.addCase(deleteSalesRep.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getSupplierTaxExemptionTeamExtraReducer = (
  builder: ActionReducerMapBuilder<TaxExemptionTeamInitialState>
) => {
  const reducerId = 'getSupplierTaxExemptionTeam';
  builder.addCase(getSupplierTaxExemptionTeam.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getSupplierTaxExemptionTeam.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.taxExemptionTeam = action.payload.data as TaxExemptionTeamMember[];
      }
    }
  );
  builder.addCase(getSupplierTaxExemptionTeam.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const createTaxExemptionTeamMemberExtraReducer = (
  builder: ActionReducerMapBuilder<TaxExemptionTeamInitialState>
) => {
  const reducerId = 'createTaxExemptionTeamMember';
  builder.addCase(createTaxExemptionTeamMember.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    createTaxExemptionTeamMember.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.taxExemptionTeam = action.payload.data as TaxExemptionTeamMember[];
      }
    }
  );
  builder.addCase(createTaxExemptionTeamMember.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const updateTaxExemptionTeamMemberExtraReducer = (
  builder: ActionReducerMapBuilder<TaxExemptionTeamInitialState>
) => {
  const reducerId = 'updateTaxExemptionTeamMember';
  builder.addCase(updateTaxExemptionTeamMember.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    updateTaxExemptionTeamMember.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.taxExemptionTeam = action.payload.data as TaxExemptionTeamMember[];
      }
    }
  );
  builder.addCase(updateTaxExemptionTeamMember.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const deleteTaxExemptionTeamMemberExtraReducer = (
  builder: ActionReducerMapBuilder<TaxExemptionTeamInitialState>
) => {
  const reducerId = 'deleteTaxExemptionTeamMember';
  builder.addCase(deleteTaxExemptionTeamMember.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    deleteTaxExemptionTeamMember.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.taxExemptionTeam = action.payload.data as TaxExemptionTeamMember[];
      }
    }
  );
  builder.addCase(deleteTaxExemptionTeamMember.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getSupplierBranchesExtraReducer = (
  builder: ActionReducerMapBuilder<SupplierBranchesInitialState>
) => {
  const reducerId = 'getSupplierBranches';
  builder.addCase(getSupplierBranches.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getSupplierBranches.fulfilled,
    (
      state,
      action: PayloadAction<AppServerResponse | null, string, { arg: { supplierId: string } }>
    ) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.loadedSupplierId = action.meta.arg.supplierId;
      if (action.payload && action.payload.success && action.payload?.data) {
        state.branches = action.payload.data as SupplierBranch[];
      }
    }
  );
  builder.addCase(
    getSupplierBranches.rejected,
    (state, action: { meta: { arg: { supplierId: string } } }) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      state.loadedSupplierId = action.meta.arg.supplierId;
    }
  );
};

export const updateSupplierBranchesExtraReducer = (
  builder: ActionReducerMapBuilder<SupplierBranchesInitialState>
) => {
  const reducerId = 'updateSupplierBranches';
  builder.addCase(updateSupplierBranches.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    updateSupplierBranches.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.branches = action.payload.data as SupplierBranch[];
      }
    }
  );
  builder.addCase(updateSupplierBranches.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};
